/**
 * Finds the first tabbable (keyboard-focusable) element within a document,
 * shadow root, or element subtree, piercing into nested shadow roots. Returns
 * `null` when no tabbable element exists.
 */
export const getFirstTabbable = (
  root: Document | ShadowRoot | Element,
): HTMLElement | null => {
  const walker = document.createTreeWalker(
    root as Node,
    NodeFilter.SHOW_ELEMENT,
  );

  let node = walker.nextNode() as HTMLElement | null;
  while (node) {
    const tag = node.tagName.toLowerCase();
    const isNativelyFocusable = [
      'button',
      'input',
      'select',
      'textarea',
      'a',
      'iframe',
    ].includes(tag);
    const hasTabIndex =
      node.getAttribute('tabindex') !== null && node.tabIndex >= 0;
    const isRoleButton = node.getAttribute('role') === 'button';
    const isDisabled = (node as HTMLButtonElement).disabled;

    if (!isDisabled && (isNativelyFocusable || hasTabIndex || isRoleButton)) {
      return node;
    }

    // Pierce shadow root
    if (node.shadowRoot) {
      const shadowResult = getFirstTabbable(node.shadowRoot);
      if (shadowResult) return shadowResult;
    }

    node = walker.nextNode() as HTMLElement | null;
  }
  return null;
};

/**
 * Forwards focus to the first tabbable element within the container with the
 * given ID, if focus is not already on that element. Call this when focus
 * lands programmatically on the iframe body and needs to be moved to the
 * actual interactive button.
 */
export const focusFirstTabbable = (containerId: string): void => {
  const container = document.getElementById(containerId);
  const focusTarget = container && getFirstTabbable(container);
  if (focusTarget && document.activeElement !== focusTarget) {
    focusTarget.focus();
  }
};
